import type { UnitDomainGraphResponse } from '$lib/types/domain_graph';
import type { DomainGraphDINChannel } from '$lib/types/din_channel';
import type { DomainGraphRCUChannel } from '$lib/types/rcu_channel';

export type MappedChannel = {
	id: number;
	channel_number: number;
	load_id: number;
	source: 'rcu' | 'din';
	rcu_id?: number;
	din_module_id?: number;
	bus_no?: number | null;
};

export type ChannelMaps = {
	channelsByLoadId: Map<number, MappedChannel>;
	mappedRCUChannels: DomainGraphRCUChannel[];
	emptyRCUChannels: DomainGraphRCUChannel[];
	mappedDINChannels: DomainGraphDINChannel[];
	emptyDINChannels: DomainGraphDINChannel[];
	allMappedChannels: MappedChannel[];
};

type LegacyHardware = {
	rcu?: NonNullable<UnitDomainGraphResponse['hardware']['rcu']> | NonNullable<UnitDomainGraphResponse['hardware']['rcu']>[];
	rcus?: NonNullable<UnitDomainGraphResponse['hardware']['rcu']>[];
};

function extractRcuList(domainGraph: UnitDomainGraphResponse): NonNullable<UnitDomainGraphResponse['hardware']['rcu']>[] {
	const hardware = (domainGraph.hardware as unknown as LegacyHardware) ?? {};
	if (Array.isArray(hardware.rcus) && hardware.rcus.length) return hardware.rcus;
	if (Array.isArray(hardware.rcu) && hardware.rcu.length)
		return hardware.rcu as NonNullable<UnitDomainGraphResponse['hardware']['rcu']>[];
	if (hardware.rcu && !Array.isArray(hardware.rcu))
		return [hardware.rcu as NonNullable<UnitDomainGraphResponse['hardware']['rcu']>];
	return [];
}

export function buildChannelMaps(domainGraph: UnitDomainGraphResponse): ChannelMaps {
	const rcuList = extractRcuList(domainGraph);
	const channelsByLoadId = new Map<number, MappedChannel>();
	const mappedRCUChannels: DomainGraphRCUChannel[] = [];
	const emptyRCUChannels: DomainGraphRCUChannel[] = [];
	const mappedDINChannels: DomainGraphDINChannel[] = [];
	const emptyDINChannels: DomainGraphDINChannel[] = [];
	const allMappedChannels: MappedChannel[] = [];

	for (const rcu of rcuList) {
		for (const ch of rcu.channels ?? []) {
			if (ch.load_id !== null) {
				mappedRCUChannels.push(ch);
				const mapped: MappedChannel = {
					id: ch.id,
					channel_number: ch.channel_number,
					load_id: ch.load_id,
					source: 'rcu',
					rcu_id: rcu.id
				};
				channelsByLoadId.set(ch.load_id, mapped);
				allMappedChannels.push(mapped);
			} else {
				emptyRCUChannels.push(ch);
			}
		}

		for (const dinModule of rcu.din_modules ?? []) {
			for (const ch of dinModule.channels ?? []) {
				if (ch.load_id !== null) {
					mappedDINChannels.push(ch);
					const mapped: MappedChannel = {
						id: ch.id,
						channel_number: ch.channel_number,
						load_id: ch.load_id,
						source: 'din',
						din_module_id: dinModule.id
					};
					channelsByLoadId.set(ch.load_id, mapped);
					allMappedChannels.push(mapped);
				} else {
					emptyDINChannels.push(ch);
				}
			}
		}
	}

	return {
		channelsByLoadId,
		mappedRCUChannels,
		emptyRCUChannels,
		mappedDINChannels,
		emptyDINChannels,
		allMappedChannels
	};
}

export function getChannelLabel(
	mapped: MappedChannel | undefined,
	maps: { rcuName?: string; dinById: Map<number, { module_type: string; address: number }> }
): string {
	if (!mapped) return 'Unassigned';

	if (mapped.source === 'rcu') {
		return `RCU / Ch ${mapped.channel_number}`;
	}

	const din = mapped.din_module_id ? maps.dinById.get(mapped.din_module_id) : undefined;
	if (din) {
		const moduleType = String(din.module_type)
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, (m) => m.toUpperCase());
		const bus = mapped.bus_no ? ` Bus ${mapped.bus_no}` : '';
		return `${moduleType} DIN ${din.address} / Ch ${mapped.channel_number}${bus}`;
	}

	return `DIN / Ch ${mapped.channel_number}`;
}
